import { useEffect } from "react";
import { clarity } from "react-microsoft-clarity";
import ReactGA from "react-ga4";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Index from "@/pages/Index";
import IntakeV2Page from "@/intake-v2/IntakeV2Page";
import ContinuePage from "@/intake-v2/ContinuePage";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import { LanguageProvider } from "@/providers/LanguageProvider";
import { RootRedirect } from "@/components/routing/RootRedirect";
import { LegacyPrivacyPolicyRedirect } from "@/components/routing/LegacyPrivacyPolicyRedirect";
import { LocaleBranch } from "@/components/routing/LocaleBranch";
import { captureReferralCode } from "@/lib/referralTracker";

const queryClient = new QueryClient();
let clarityInitialized = false;

export function App() {
  useEffect(() => {
    // Capture referral code from URL search query if present
    captureReferralCode();

    const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID?.trim();
    const isBrowser = typeof window !== "undefined";
    const isLocalhost =
      isBrowser &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

    if (!clarityId && !isLocalhost) {
      console.warn("[Analytics] VITE_CLARITY_PROJECT_ID is missing. Clarity is disabled.");
    }

    if (clarityId && !clarityInitialized) {
      clarity.init(clarityId);
      clarityInitialized = true;
    }

    const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (gaMeasurementId) {
      ReactGA.initialize(gaMeasurementId);
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <LanguageProvider>
              <AnalyticsTracker />
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/en/" element={<Navigate to="/en" replace />} />
                <Route path="/es/" element={<Navigate to="/es" replace />} />
                <Route
                  path="/privacy-policy"
                  element={<LegacyPrivacyPolicyRedirect />}
                />
                <Route path="/auth/magic-link/callback" element={<AuthCallback />} />
                <Route path="/en" element={<LocaleBranch />}>
                  <Route index element={<IntakeV2Page />} />
                  <Route path="continue/:formUid" element={<ContinuePage />} />
                  <Route path="v1" element={<Index />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="/es" element={<LocaleBranch />}>
                  <Route index element={<IntakeV2Page />} />
                  <Route path="continue/:formUid" element={<ContinuePage />} />
                  <Route path="v1" element={<Index />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LanguageProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
